import React, { useState, useEffect, useMemo } from 'react';
import { 
  Button,
  Dialog,
  DialogActions,
  DialogContent, 
  DialogTitle, 
  FormControlLabel, 
  List, 
  ListItem, 
  Typography, 
  Box, 
  Checkbox, 
  CircularProgress 
} from '@mui/material';

import { getAccess, updateAccess } from '../../http/accessAPI';
import ChatCategoryAccessControl from '../ChatCategoryAccessControl';

const AccessLevelDialog = ({ adminId, isOwner, onClose }) => {
  const [open, setOpen] = useState(true); // Диалог открыт по умолчанию
  const [selectedSections, setSelectedSections] = useState([]);
  const [patientAccess, setPatientAccess] = useState({ earnPoints: false, subtractPoints: false });
  const [chatCategoryIds, setChatCategoryIds] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const handleClose = () => {
    setOpen(false);
    onClose(); // Передаем onClose в родительский компонент
  };

  const handleCheckboxChange = (event) => {
    const { checked, value } = event.target;
    setSelectedSections((prevSelectedSections) => {
      if (checked) {
        return [...prevSelectedSections, value];
      } else {
        return prevSelectedSections.filter((section) => section !== value);
      }
    });

    if (value === 'patient' && !checked) {
      setPatientAccess({ earnPoints: false, subtractPoints: false });
      setSelectedSections((prevSelectedSections) => prevSelectedSections.filter((section) => section !== 'earnPoints'));
    }
    
    // При снятии галочки "Чат с менеджером" очищаем категории
    if (value === 'chat' && !checked) {
      setChatCategoryIds([]);
    }
  };

  const handlePatientCheckboxChange = (event) => {
    const { checked, value } = event.target;

    setPatientAccess((prevAccess) => {
      const newAccess = { ...prevAccess, [value]: checked };

      setSelectedSections((prevSelectedSections) => {
        const updatedSections = [...prevSelectedSections];

        if (newAccess.earnPoints && !prevSelectedSections.includes('earnPoints')) {
          updatedSections.push('earnPoints');
        } else if (!newAccess.earnPoints && prevSelectedSections.includes('earnPoints')) {
          const index = updatedSections.indexOf('earnPoints');
          updatedSections.splice(index, 1);
        }

        if (newAccess.subtractPoints && !prevSelectedSections.includes('subtractPoints')) {
          updatedSections.push('subtractPoints');
        } else if (!newAccess.subtractPoints && prevSelectedSections.includes('subtractPoints')) {
          const index = updatedSections.indexOf('subtractPoints');
          updatedSections.splice(index, 1);
        }

        return updatedSections;
      });

      return newAccess;
    });
  };

  const handleSubmit = async () => {
    try {
      await updateAccess(adminId, selectedSections);
      // Категории чатов сохраняются автоматически в ChatCategoryAccessControl
    } catch (error) {
      console.error('Ошибка при изменении доступа:', error);
    }
    handleClose();
  };

  const sections = useMemo(() => [
    { id: 'settings', name: 'Настройки' },
    { id: 'chat', name: 'Чат с менеджером' },
    { id: 'manageChatCategories', name: 'Управление категориями чатов' },
    { id: 'news', name: 'Новости' },
    { id: 'promotions', name: 'Акции' },
    { id: 'exampleOfWorks', name: 'Услуги' },
    { id: 'doctor', name: 'Врачи' },
    { id: 'scoreHistory', name: 'История баллов' },
    { id: 'mailing', name: 'Рассылка' },
    { id: 'review', name: 'Отзывы' },
    { id: 'patient', name: 'Пациенты' },
    { id: 'academy', name: 'Академия' },
  ], []);

  useEffect(() => {
    const fetchAccess = async () => {
      try {
        setIsLoading(true);
        const access = await getAccess(adminId);
        const initiallySelected = sections
          .filter((section) => access[section.id])
          .map((section) => section.id);

        setSelectedSections(initiallySelected);
        setPatientAccess({
          earnPoints: access.earnPoints,
          subtractPoints: access.subtractPoints,
        });
      } catch (error) {
        console.error('Ошибка при получении доступа:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAccess();
  }, [adminId, sections]);

  if (isLoading) return <CircularProgress />;

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Настройки доступа админ.панели</DialogTitle>
      <DialogContent>
        <Typography variant="h6">Выберите разделы:</Typography>
        <List>
          {sections.map((section) => (
            <ListItem sx={{ padding: 0, display: 'block' }} key={section.id}>
              {section.id === 'patient' ? (
                <>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedSections.includes('patient')}
                        onChange={handleCheckboxChange}
                        value="patient"
                      />
                    }
                    label={section.name}
                  />
                  {selectedSections.includes('patient') && (
                    <Box sx={{ paddingLeft: 3 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            value="earnPoints"
                            checked={patientAccess.earnPoints}
                            onChange={handlePatientCheckboxChange}
                          />
                        }
                        label="Начислить баллы"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            value="subtractPoints"
                            checked={patientAccess.subtractPoints}
                            onChange={handlePatientCheckboxChange}
                          />
                        }
                        label="Списать баллы"
                      />
                    </Box>
                  )}
                </>
              ) : section.id === 'chat' ? (
                <>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedSections.includes('chat')}
                        onChange={handleCheckboxChange}
                        value="chat"
                      />
                    }
                    label={section.name}
                  />
                  {selectedSections.includes('chat') && (
                    <ChatCategoryAccessControl
                      adminId={adminId}
                      onCategoriesChange={setChatCategoryIds}
                    />
                  )}
                </>
              ) : (
                <FormControlLabel
                  control={
                    <Checkbox
                      value={section.id}
                      checked={selectedSections.includes(section.id)}
                      onChange={handleCheckboxChange}
                    />
                  }
                  label={section.name}
                />
              )}
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleSubmit} color="primary" variant="contained">
          Сохранить
        </Button>
        <Button onClick={handleClose} color="error" variant="contained">
          Отмена
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AccessLevelDialog;
